/**
 * UAPC Anytime - Vendor Inventory Page
 * @module VendorInventory
 * @author Saif Ali Khan - Full Stack Developer
 * @author Sohail - Backend Developer
 * @description Inventory management for vendors
 * @version 1.0.0
 * Git: Saif Ali Khan <saif@linesoft.com> - Inventory CRUD
 * Git: Sohail <sohail@linesoft.com> - Backend integration
 */

import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import VendorLayout from '../../components/VendorLayout';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VendorInventory = () => {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [formData, setFormData] = useState({
    part_number: '',
    name: '',
    description: '',
    category: '',
    price: '',
    quantity: '',
    condition: 'used',
    make: '',
    model: '',
    year_from: '',
    year_to: '',
    location: '',
    image_url: ''
  });

  useEffect(() => {
    fetchParts();
  }, []);

  const fetchParts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vendor/parts`);
      setParts(response.data);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      part_number: '',
      name: '',
      description: '',
      category: '',
      price: '',
      quantity: '',
      condition: 'used',
      make: '',
      model: '',
      year_from: '',
      year_to: '',
      location: '',
      image_url: ''
    });
    setEditingPart(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...formData,
      price: parseFloat(formData.price),
      quantity: parseInt(formData.quantity),
      year_from: formData.year_from ? parseInt(formData.year_from) : null,
      year_to: formData.year_to ? parseInt(formData.year_to) : null
    };

    try {
      if (editingPart) {
        await axios.put(`${API_URL}/api/parts/${editingPart.id}`, data);
        toast.success('Part updated successfully');
      } else {
        await axios.post(`${API_URL}/api/parts`, data);
        toast.success('Part added successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchParts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save part');
    }
  };

  const handleEdit = (part) => {
    setEditingPart(part);
    setFormData({
      part_number: part.part_number,
      name: part.name,
      description: part.description || '',
      category: part.category,
      price: part.price.toString(),
      quantity: part.quantity.toString(),
      condition: part.condition,
      make: part.make || '',
      model: part.model || '',
      year_from: part.year_from?.toString() || '',
      year_to: part.year_to?.toString() || '',
      location: part.location || '',
      image_url: part.image_url || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (partId) => {
    if (!window.confirm('Are you sure you want to delete this part?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/parts/${partId}`);
      toast.success('Part deleted');
      fetchParts();
    } catch (error) {
      toast.error('Failed to delete part');
    }
  };

  const filteredParts = parts.filter(part => 
    part.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    part.part_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <VendorLayout>
      <div className="p-8" data-testid="vendor-inventory">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading font-black text-3xl text-slate-900 dark:text-white uppercase tracking-tight">
            Inventory Management
          </h1>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-industrial" data-testid="add-part-btn">
                <Plus className="w-5 h-5 mr-2" />
                Add Part
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-xl uppercase">
                  {editingPart ? 'Edit Part' : 'Add New Part'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Part Number *</Label>
                    <Input
                      value={formData.part_number}
                      onChange={(e) => setFormData({ ...formData, part_number: e.target.value })}
                      placeholder="e.g., ENG-001"
                      required
                      disabled={!!editingPart}
                      data-testid="input-part-number"
                    />
                  </div>
                  <div>
                    <Label>Category *</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                      <SelectTrigger data-testid="input-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Engine">Engine</SelectItem>
                        <SelectItem value="Transmission">Transmission</SelectItem>
                        <SelectItem value="Brakes">Brakes</SelectItem>
                        <SelectItem value="Suspension">Suspension</SelectItem>
                        <SelectItem value="Electrical">Electrical</SelectItem>
                        <SelectItem value="Body Parts">Body Parts</SelectItem>
                        <SelectItem value="Interior">Interior</SelectItem>
                        <SelectItem value="Exhaust">Exhaust</SelectItem>
                        <SelectItem value="Cooling">Cooling</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Part Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., V8 Engine Block"
                    required
                    data-testid="input-name"
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Detailed description of the part..."
                    rows={3}
                    data-testid="input-description"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Price ($) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      data-testid="input-price"
                    />
                  </div>
                  <div>
                    <Label>Quantity *</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      required
                      data-testid="input-quantity"
                    />
                  </div>
                  <div>
                    <Label>Condition *</Label>
                    <Select value={formData.condition} onValueChange={(v) => setFormData({ ...formData, condition: v })}>
                      <SelectTrigger data-testid="input-condition">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="used">Used</SelectItem>
                        <SelectItem value="refurbished">Refurbished</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Vehicle Make</Label>
                    <Input
                      value={formData.make}
                      onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                      placeholder="e.g., Toyota"
                      data-testid="input-make"
                    />
                  </div>
                  <div>
                    <Label>Vehicle Model</Label>
                    <Input
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      placeholder="e.g., Camry"
                      data-testid="input-model"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Year From</Label>
                    <Input
                      type="number"
                      min="1900"
                      max="2030"
                      value={formData.year_from}
                      onChange={(e) => setFormData({ ...formData, year_from: e.target.value })}
                      placeholder="e.g., 2015"
                      data-testid="input-year-from"
                    />
                  </div>
                  <div>
                    <Label>Year To</Label>
                    <Input
                      type="number"
                      min="1900"
                      max="2030"
                      value={formData.year_to}
                      onChange={(e) => setFormData({ ...formData, year_to: e.target.value })}
                      placeholder="e.g., 2020"
                      data-testid="input-year-to"
                    />
                  </div>
                </div>

                <div>
                  <Label>Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Houston, TX"
                    data-testid="input-location"
                  />
                </div>

                <div>
                  <Label>Image URL</Label>
                  <Input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://..."
                    data-testid="input-image-url"
                  />
                </div>

                <div className="flex justify-end gap-4 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-part-btn">
                    {editingPart ? 'Update Part' : 'Add Part'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search by name or part number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 bg-white dark:bg-slate-900"
            data-testid="search-inventory"
          />
        </div>

        {/* Parts Table */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-100 dark:bg-slate-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-heading font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Part #</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Price</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Stock</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Condition</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Synced</th>
                  <th className="px-4 py-3 text-right text-xs font-heading font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan="8" className="px-4 py-4">
                        <div className="h-6 bg-slate-200 dark:bg-slate-700 animate-pulse" />
                      </td>
                    </tr>
                  ))
                ) : filteredParts.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-slate-500">
                      {searchQuery ? 'No parts match your search' : 'No parts in inventory. Add your first part!'}
                    </td>
                  </tr>
                ) : (
                  filteredParts.map((part, index) => (
                    <tr key={part.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`part-row-${index}`}>
                      <td className="px-4 py-4 font-mono text-sm">{part.part_number}</td>
                      <td className="px-4 py-4 font-medium text-slate-900 dark:text-white">{part.name}</td>
                      <td className="px-4 py-4 text-slate-600 dark:text-slate-400">{part.category}</td>
                      <td className="px-4 py-4 font-heading font-bold text-blue-600">${part.price.toFixed(2)}</td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 text-xs font-bold ${
                          part.quantity === 0 ? 'bg-red-100 text-red-800' :
                          part.quantity < 5 ? 'bg-orange-100 text-orange-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {part.quantity}
                        </span>
                      </td>
                      <td className="px-4 py-4 capitalize text-slate-600 dark:text-slate-400">{part.condition}</td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 text-xs font-bold ${
                          part.mvs_synced ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {part.mvs_synced ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleEdit(part)}
                            data-testid={`edit-part-${index}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(part.id)}
                            data-testid={`delete-part-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </VendorLayout>
  );
};

export default VendorInventory;
